import React, { useState } from 'react';
import { Lead, LeadStatus } from '../../types/lead';
import {
  BuildingOffice2Icon,
  ChatBubbleLeftIcon,
  ClockIcon,
  ArrowRightCircleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

/**
 * Lead pipeline-tábla: oszloponként egy státusz, a kártya húzásával vált státuszt.
 * Natív HTML5 drag & drop – asztali nézetre; mobilon a lista/kártya nézet marad.
 */

export const LEAD_STAGES: { id: LeadStatus; label: string; dot: string; soft: string }[] = [
  { id: 'new',         label: 'Új',                dot: 'bg-sky-500',     soft: 'bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300' },
  { id: 'contacted',   label: 'Kapcsolatba lépve', dot: 'bg-amber-500',   soft: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300' },
  { id: 'qualified',   label: 'Minősített',        dot: 'bg-green-500',   soft: 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300' },
  { id: 'unqualified', label: 'Nem minősített',    dot: 'bg-gray-400',    soft: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400' },
  { id: 'converted',   label: 'Konvertált',        dot: 'bg-primary-600', soft: 'bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-300' },
];

const SOURCE_LABEL: Record<string, string> = {
  website: 'Weboldal', manual: 'Kézi', referral: 'Ajánlás', social_media: 'Közösségi média',
  advertisement: 'Hirdetés', google_sheets: 'Google Sheets', other: 'Egyéb',
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export const followUpState = (d?: Date): { text: string; tone: 'late' | 'today' | 'soon' | 'none' } => {
  if (!d) return { text: '', tone: 'none' };
  const today = startOfDay(new Date());
  const day = startOfDay(d);
  const diff = Math.round((day.getTime() - today.getTime()) / 86400000);
  if (diff < 0) return { text: `${-diff} napja lejárt`, tone: 'late' };
  if (diff === 0) return { text: 'ma', tone: 'today' };
  if (diff === 1) return { text: 'holnap', tone: 'soon' };
  return { text: d.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' }), tone: 'soon' };
};

const initials = (name?: string) =>
  (name || '').split(' ').filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || '?';

interface Props {
  leads: Lead[];
  showConverted: boolean;
  onStatusChange: (leadId: string, status: LeadStatus) => void;
  onOpen: (lead: Lead) => void;
  onConvert: (lead: Lead) => void;
  onViewComments: (lead: Lead) => void;
  commentCounts?: Record<string, number>;
}

const LeadKanban: React.FC<Props> = ({ leads, showConverted, onStatusChange, onOpen, onConvert, onViewComments, commentCounts = {} }) => {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<LeadStatus | null>(null);

  const stages = LEAD_STAGES.filter(s => showConverted || s.id !== 'converted');

  const handleDrop = (e: React.DragEvent, status: LeadStatus) => {
    e.preventDefault();
    const id = dragId || e.dataTransfer.getData('text/plain');
    if (!id) return;
    const lead = leads.find(l => l.id === id);
    if (lead && lead.status !== status) onStatusChange(lead.id, status);
    setDragId(null);
    setOverCol(null);
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1 min-h-[60vh] items-start [scrollbar-width:thin]">
      {stages.map(stage => {
        const items = leads.filter(l => l.status === stage.id);
        const isOver = overCol === stage.id;
        return (
          <div
            key={stage.id}
            onDragEnter={(e) => { e.preventDefault(); setOverCol(stage.id); }}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overCol !== stage.id) setOverCol(stage.id); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOverCol(null); }}
            onDrop={(e) => handleDrop(e, stage.id)}
            className={`flex-shrink-0 w-[272px] flex flex-col rounded-[10px] border transition-colors ${
              isOver ? 'border-primary-400 bg-primary-50/60 dark:bg-primary-500/10' : 'border-[var(--crm-border)] bg-[var(--crm-bg)]'
            }`}
          >
            {/* Oszlopfej */}
            <div className="flex items-center gap-2 px-3 py-2.5">
              <span className={`w-2 h-2 rounded-full ${stage.dot}`} />
              <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{stage.label}</span>
              <span className="ml-auto text-xs text-gray-500 dark:text-gray-400 tabular-nums">{items.length}</span>
            </div>

            {/* Kártyák */}
            <div className="flex flex-col gap-2 px-2 pb-2 min-h-[80px]">
              {items.length === 0 && (
                <div className="text-xs text-gray-400 text-center py-6 border border-dashed border-[var(--crm-border)] rounded-lg">
                  {isOver ? 'Engedd el itt' : 'Nincs lead'}
                </div>
              )}
              {items.map(lead => {
                const fu = followUpState(lead.nextFollowUpAt);
                const score = typeof lead.score === 'number' ? Math.max(0, Math.min(100, lead.score)) : null;
                return (
                  <div
                    key={lead.id}
                    draggable
                    onDragStart={(e) => {
                      setDragId(lead.id);
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', lead.id); // Firefox enélkül el sem indítja a húzást
                    }}
                    onDragEnd={() => { setDragId(null); setOverCol(null); }}
                    onClick={() => onOpen(lead)}
                    className={`group crm-card p-3 cursor-grab active:cursor-grabbing hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-all ${
                      dragId === lead.id ? 'opacity-40' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50 truncate leading-tight">{lead.name}</div>
                        {lead.company && (
                          <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                            <BuildingOffice2Icon className="w-3 h-3 flex-shrink-0" />{lead.company}
                          </div>
                        )}
                      </div>
                      {lead.assignedToName && (
                        <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] font-semibold text-gray-700 dark:text-gray-200 flex items-center justify-center flex-shrink-0" title={lead.assignedToName}>
                          {initials(lead.assignedToName)}
                        </div>
                      )}
                    </div>

                    {/* Pontszám */}
                    {score !== null && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                          <div className={`h-full rounded-full ${score >= 70 ? 'bg-green-500' : score >= 40 ? 'bg-amber-500' : 'bg-gray-400'}`} style={{ width: `${score}%` }} />
                        </div>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">{score}</span>
                      </div>
                    )}

                    {/* Lábléc: forrás, követés, gyors műveletek */}
                    <div className="mt-2 flex items-center gap-1.5 text-[11px]">
                      <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 truncate">
                        {SOURCE_LABEL[lead.source] || lead.source}
                      </span>
                      {(commentCounts[lead.id] || 0) > 0 && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300 tabular-nums" title="Megjegyzések">
                          <ChatBubbleLeftIcon className="w-3 h-3" />{commentCounts[lead.id]}
                        </span>
                      )}
                      {fu.tone !== 'none' && (
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded tabular-nums ${
                          fu.tone === 'late' ? 'bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-400 font-medium'
                          : fu.tone === 'today' ? 'bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 font-medium'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                        }`}>
                          <ClockIcon className="w-3 h-3" />{fu.text}
                        </span>
                      )}
                      <span className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); onViewComments(lead); }} className="p-1 rounded text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800" title="Megjegyzések">
                          <ChatBubbleLeftIcon className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onOpen(lead); }} className="p-1 rounded text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800" title="Szerkesztés">
                          <PencilSquareIcon className="w-3.5 h-3.5" />
                        </button>
                        {lead.status !== 'converted' && (
                          <button onClick={(e) => { e.stopPropagation(); onConvert(lead); }} className="p-1 rounded text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/15" title="Ügyféllé alakítás">
                            <ArrowRightCircleIcon className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LeadKanban;
